import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../services/apiService'

const UserProfile = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    fetchUserProfile()
  }, [id])

  const fetchUserProfile = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/client-admin/users/${id}`)
      if (response.data.success) {
        setUser(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
      alert('Failed to fetch user profile')
    } finally {
      setLoading(false)
    }
  }


  if (loading) {
    return (
      <div className="user-profile-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading user profile...</p>
        </div>
        <style>{`
          .user-profile-page {
            padding: 0;
            width: 100%;
            min-height: 100vh;
            background: #f3f4f6;
          }
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 50vh;
            color: #6b7280;
          }
          .spinner {
            width: 50px;
            height: 50px;
            border: 5px solid #e5e7eb;
            border-top: 5px solid var(--primary-color, #022a5e);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
            margin-bottom: 1rem;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="user-profile-page">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>User not found</p>
          <button onClick={() => navigate('/admin/users')} className="btn-primary">
            <i className="fas fa-arrow-left"></i> Back to Users
          </button>
        </div>
        <style>{`
          .user-profile-page {
            padding: 0;
            width: 100%;
            min-height: 100vh;
            background: #f3f4f6;
          }
          .error-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 50vh;
            color: #6b7280;
          }
          .error-container i {
            font-size: 4rem;
            color: #ef4444;
            margin-bottom: 1rem;
          }
          .error-container p {
            font-size: 1.25rem;
            margin-bottom: 1.5rem;
          }
          .btn-primary {
            padding: 0.75rem 1.5rem;
            background: var(--primary-color, #022a5e);
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="user-profile-page">
      {/* Page Header with gradient background */}
      <div className="page-header-banner">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-user"></i>
          </div>
          <div className="header-text">
            <h1>{user.association_name || user.full_name || 'User Profile'}</h1>
            <p>View user and association details</p>
          </div>
        </div>
        <div className="header-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="btn-edit-user" onClick={() => navigate(`/admin/users/edit/${id}`)}>
            <i className="fas fa-edit"></i> Edit User
          </button>
          <button className="btn-back" onClick={() => navigate('/admin/users')}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-icon total">
            <i className="fas fa-building"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value association-name">{user.association_name || '-'}</span>
            <span className="stat-label">Association</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon state">
            <i className="fas fa-map-marker-alt"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value">{user.state_name || user.state || '-'}</span>
            <span className="stat-label">State</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon status">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-info">
            <span className={`status-badge status-${user.status}`}>{user.status || 'Active'}</span>
            <span className="stat-label">Account Status</span>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="content-section">
        <div className="details-container">
          {/* Association Information Section */}
          <div className="detail-section">
            <h3><i className="fas fa-building"></i> Association Information</h3>
            <div className="detail-grid">
              <div className="detail-item full-width">
                <label>Association Name</label>
                <span className="detail-value highlight">{user.association_name || '-'}</span>
              </div>
              <div className="detail-item">
                <label>State</label>
                <span className="detail-value">
                  <span className="state-badge">{user.state_name || user.state || '-'}</span>
                </span>
              </div>
              <div className="detail-item">
                <label>President</label>
                <span className="detail-value">{user.president || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Secretary</label>
                <span className="detail-value">{user.secretary || '-'}</span>
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="detail-section">
            <h3><i className="fas fa-phone-alt"></i> Contact Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Official Contact No</label>
                <span className="detail-value">
                  <i className="fas fa-phone icon-sm"></i>
                  {user.official_contact || user.phone || '-'}
                </span>
              </div>
              <div className="detail-item">
                <label>Official Email ID</label>
                <span className="detail-value">
                  <i className="fas fa-envelope icon-sm"></i>
                  {user.official_email || user.email || '-'}
                </span>
              </div>
              <div className="detail-item full-width">
                <label>Address</label>
                <span className="detail-value">
                  <i className="fas fa-map-marker-alt icon-sm"></i>
                  {user.address || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Login Credentials Section */}
          <div className="detail-section">
            <h3><i className="fas fa-lock"></i> Login Credentials</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Username</label>
                <span className="detail-value username-badge">
                  <i className="fas fa-user icon-sm"></i>
                  {user.username || '-'}
                </span>
              </div>
              <div className="detail-item">
                <label>Password</label>
                <div className="password-field">
                  <span className="detail-value password-value">
                    <i className="fas fa-lock icon-sm"></i>
                    {showPassword
                      ? (user.plain_password || 'Password not stored')
                      : '••••••••'}
                  </span>
                  <button
                    className="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? 'Hide Password' : 'Show Password'}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information Section */}
          <div className="detail-section">
            <h3><i className="fas fa-info-circle"></i> Account Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Account Status</label>
                <span className="detail-value">
                  <span className={`status-badge-lg status-${user.status}`}>
                    <i className={`fas ${user.status === 'active' ? 'fa-check-circle' : 'fa-clock'}`}></i>
                    {user.status || 'Active'}
                  </span>
                </span>
              </div>
              <div className="detail-item">
                <label>Account Created</label>
                <span className="detail-value">
                  <i className="fas fa-calendar-plus icon-sm"></i>
                  {user.created_at || '-'}
                </span>
              </div>
              <div className="detail-item">
                <label>Last Login</label>
                <span className="detail-value">
                  <i className="fas fa-sign-in-alt icon-sm"></i>
                  {user.last_login_at || 'Never'}
                </span>
              </div>
              <div className="detail-item">
                <label>Last Updated</label>
                <span className="detail-value">
                  <i className="fas fa-edit icon-sm"></i>
                  {user.updated_at || '-'}
                </span>
              </div>
            </div>
          </div>
        </div>

        </div>

      <style>{`
        .user-profile-page {
          padding: 0;
          width: 100%;
          min-height: 100vh;
          background: #f3f4f6;
        }

        .page-header-banner {
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          width: 100%;
        }

        .btn-back {
          height: 40px;
          padding: 0 18px;
          border-radius: 8px;
          border: none;
          background: #c00f15;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          color: #fff;
          font-weight: 600;
          font-size: 0.9rem;
          white-space: nowrap;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .btn-back:hover {
          background: #a00d12;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex: 1;
        }

        .header-icon {
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .header-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .header-text h1 {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .header-text p {
          color: rgba(255,255,255,0.7);
          margin: 0;
          font-size: 0.875rem;
        }

        .header-actions {
          display: flex;
          gap: 0.75rem;
        }

        .btn-edit-user {
          padding: 0.75rem 1.5rem;
          background: #c00f15;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .btn-edit-user:hover {
          background: #a00d12;
          transform: translateY(-2px);
        }

        /* Stats Section */
        .stats-section {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          padding: 1.5rem 2rem;
          margin-top: -1rem;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          transition: all 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
        }

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-icon i {
          font-size: 1.25rem;
        }

        .stat-icon.total {
          background: rgba(2, 42, 94, 0.1);
          color: var(--primary-color, #022a5e);
        }

        .stat-icon.state {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .stat-icon.status {
          background: #dcfce7;
          color: #16a34a;
        }

        .stat-value.association-name {
          font-size: 1rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 200px;
        }

        .stat-info {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
        }

        .stat-label {
          font-size: 0.8rem;
          color: #6b7280;
        }

        /* Content Section */
        .content-section {
          padding: 0 2rem 2rem;
        }

        .details-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          overflow: hidden;
        }

        .detail-section {
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .detail-section:last-child {
          border-bottom: none;
        }

        .detail-section h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 1.25rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .detail-section h3 i {
          color: var(--primary-color, #022a5e);
          font-size: 0.9rem;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .detail-item.full-width {
          grid-column: span 2;
        }

        .detail-item label {
          font-size: 0.8rem;
          font-weight: 500;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .detail-value {
          font-size: 1rem;
          color: #1f2937;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .detail-value.highlight {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--primary-color, #022a5e);
        }

        .icon-sm {
          font-size: 0.85rem;
          color: #9ca3af;
        }

        .state-badge {
          display: inline-block;
          padding: 0.35rem 0.85rem;
          background: #f1f5f9;
          color: #475569;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .username-badge {
          background: rgba(2, 42, 94, 0.05);
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          font-family: monospace;
          font-size: 0.95rem;
        }

        .password-field {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .password-value {
          background: rgba(2, 42, 94, 0.05);
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          font-family: monospace;
          font-size: 0.95rem;
        }

        .toggle-password-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          background: white;
          color: #6b7280;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .toggle-password-btn:hover {
          background: var(--primary-color, #022a5e);
          color: white;
          border-color: var(--primary-color, #022a5e);
        }

        .password-hidden {
          color: #9ca3af;
          font-family: monospace;
        }

        /* Status Badges */
        .status-badge {
          display: inline-block;
          padding: 0.35rem 0.85rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .status-badge-lg {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .status-active {
          background: #dcfce7;
          color: #166534;
        }

        .status-pending {
          background: #fef3c7;
          color: #92400e;
        }

        .status-suspended {
          background: #fee2e2;
          color: #991b1b;
        }

        .status-inactive {
          background: #f3f4f6;
          color: #6b7280;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .stats-section {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .page-header-banner {
            flex-wrap: wrap;
            gap: 1rem;
          }

          .header-actions {
            width: 100%;
            justify-content: flex-end;
          }

          .stats-section {
            grid-template-columns: 1fr;
            padding: 1rem;
          }

          .detail-grid {
            grid-template-columns: 1fr;
          }

          .detail-item.full-width {
            grid-column: span 1;
          }

          .content-section {
            padding: 0 1rem 1rem;
          }

          .detail-section {
            padding: 1.25rem 1.5rem;
          }
        }
      `}</style>
    </div>
  )
}

export default UserProfile
